/**
 * Client-safe shared types for telemetry endpoints.
 *
 * IMPORTANT: this file is imported by client-side code, so it must never import `@prisma/client` or
 * anything server-only. The provider names are therefore string literals rather than the Prisma enum.
 */
import { z } from 'zod';

import type { TranslationKey } from '../../../translation/types';

export const TelemetryProviderEnum = z.enum(['Fleetmaster', 'Suivo']);
export type TelemetryProviderName = z.infer<typeof TelemetryProviderEnum>;

/**
 * Who a connection belongs to. A carrier connection follows that carrier's trucks; a company one follows
 * our own assets — the trailers we install trackers in — and belongs to no carrier at all.
 */
export const TelemetryOwnerKindEnum = z.enum(['Carrier', 'Company']);
export type TelemetryOwnerKindName = z.infer<typeof TelemetryOwnerKindEnum>;

export const TelemetryConnectionStatusEnum = z.enum(['Active', 'Error']);
export const TelemetryLinkSourceEnum = z.enum(['Auto', 'Manual']);

export interface ConnectorFieldSpec {
  name: string;
  label: string;
  type: 'text' | 'password' | 'number';
  required: boolean;
  /**
   * A key rather than the sentence itself: the form is Dutch-first, so a provider's spec names the helper
   * line to show instead of carrying English copy into every language.
   */
  helpKey?: TranslationKey;
}

export interface ConnectorFormSpec {
  provider: TelemetryProviderName;
  displayName: string;
  /** One line saying what connecting this provider gets you, shown when choosing between them. */
  descriptionKey: TranslationKey;
  /** Non-secret fields, stored in `TelemetryConnection.config`. */
  configFields: ConnectorFieldSpec[];
  /** Secret fields, encrypted into `TelemetryConnection.credentialsEncrypted`. */
  credentialFields: ConnectorFieldSpec[];
}

/**
 * Drives the "add a connection" form generically, so a new connector needs no new form code — the
 * form renders whatever its spec declares and posts `config` and `credentials` as two objects.
 */
export const TELEMETRY_CONNECTOR_FORMS: ConnectorFormSpec[] = [
  {
    provider: 'Fleetmaster',
    displayName: 'FleetMaster',
    descriptionKey: 'liveTracking.providerFleetmaster',
    configFields: [
      {
        name: 'companyId',
        label: 'Company ID',
        type: 'number',
        required: true,
        helpKey: 'liveTracking.helpCompanyId',
      },
    ],
    credentialFields: [
      {
        name: 'apiKey',
        label: 'API key',
        type: 'password',
        required: true,
      },
    ],
  },
  {
    provider: 'Suivo',
    displayName: 'Suivo',
    descriptionKey: 'liveTracking.providerSuivo',
    configFields: [
      {
        name: 'baseUrl',
        label: 'Base URL',
        type: 'text',
        required: false,
        helpKey: 'liveTracking.helpSuivoBaseUrl',
      },
    ],
    credentialFields: [
      {
        name: 'clientId',
        label: 'Client ID',
        type: 'text',
        required: true,
      },
      {
        name: 'clientSecret',
        label: 'Client secret',
        type: 'password',
        required: true,
      },
    ],
  },
];

/**
 * The credential field whose tail `credentialsLast4` shows — the provider's secret, so a stored account is
 * recognisable without the secret itself ever leaving the server.
 *
 * Declared by typing the field `password`, which is what keeps the save endpoint provider-agnostic: it
 * derives the field from the form spec rather than naming one provider's `apiKey`.
 */
export function secretTailField(spec: ConnectorFormSpec): ConnectorFieldSpec | undefined {
  return spec.credentialFields.find((field) => field.type === 'password') ?? spec.credentialFields[0];
}
